import { Tag } from 'antd';
import i18n from '../i18n';
import type { FileTypeCode } from '../graphql/types/settlement/BillExportTypes';

/**
 * 已知 DSP 账单 feeItemName（settlement_fee_item.name）→ 稳定 ID 映射
 * 维护责任：后端新增计费项时，需同步更新此表 + i18n 三语 JSON dspBill.feeItemName.*
 */
const FEE_ITEM_NAME_TO_ID: Record<string, string> = {
  'DSP运费': 'DSP_FREIGHT',
  '同地址减免调整费': 'SAME_ADDRESS_DISCOUNT',
  'DSP特殊监控计费项': 'SPECIAL_MONITORING',
  '断更扣罚': 'STALE_PACKAGE_PENALTY',
  '不合格POD扣罚': 'NON_COMPLIANT_POD_PENALTY',
  '严重不合规POD扣罚': 'SEVERELY_VIOLATED_POD_PENALTY',
  '一般不合规POD扣罚': 'GENERAL_NON_COMPLIANT_POD',
  '恶意投递行为扣罚': 'MALICIOUS_DELIVERY_BEHAVIOR_PENALTY',
  '严重违规扣罚': 'SERIOUS_VIOLATION_PENALTY',
  'DNR扣罚': 'DNR_PENALTY',
  'DSP运费其他调整': 'DSP_FREIGHT_OTHER_ADJUSTMENT',
  '公众影响类事件扣罚': 'PUBLIC_IMPACT_EVENT_PENALTY',
  '违法行为扣罚': 'ILLEGAL_BEHAVIOR_PENALTY',
  'DSP保证金': 'DSP_DEPOSIT',
  'DSP保险费': 'DSP_INSURANCE',
};

const BILL_TYPE_COLOR_MAP: Record<string, string> = {
  DELIVERY_FEE_BILL: 'blue',
  SLA_BILL: 'green',
  DSP_DELIVERY_FEE_BILL: 'purple',
  SORTING_BILL: 'orange',
};

const BILL_STATUS_COLOR_MAP: Record<string, string> = {
  PRE_GENERATED: 'default',
  PENDING_SUMMARY: 'blue',
  INTERNAL_ALREADY: 'orange',
  ADJUSTMENT_PENDING: 'purple',
  INTERNAL_CONFIRMED: 'green',
  EXTERNAL_REJECTED: 'red',
  EXTERNAL_CONFIRMED: 'cyan',
  INVOICE_CONFIRMED: 'geekblue',
  PAID: 'success',
  LOCKED: 'red',
  CANCELED: 'default',
};

export function getBillTypeLabel(billType: string | null | undefined): string {
  if (!billType) return '-';
  return i18n.t(`enum.billType.${billType}`, { defaultValue: billType });
}

export function getBillTypeColor(billType: string | null | undefined): string {
  if (!billType) return 'default';
  return BILL_TYPE_COLOR_MAP[billType] || 'default';
}

export function renderBillTypeTag(billType: string | null | undefined) {
  return <Tag color={getBillTypeColor(billType)}>{getBillTypeLabel(billType)}</Tag>;
}

export function getBillStatusLabel(status: string | null | undefined): string {
  if (!status) return '-';
  return i18n.t(`enum.billStatus.${status}`, { defaultValue: status });
}

export function getBillStatusColor(status: string | null | undefined): string {
  if (!status) return 'default';
  return BILL_STATUS_COLOR_MAP[status] || 'default';
}

export function translateFeeItemName(name: string | null | undefined): string {
  if (!name) return '-';
  const id = FEE_ITEM_NAME_TO_ID[name];
  if (!id) {
    if (process.env.NODE_ENV !== 'production') {
      console.warn(
        `[billI18n] Unmapped feeItemName: "${name}". Add to FEE_ITEM_NAME_TO_ID and i18n JSON.`,
      );
    }
    return name;
  }
  return i18n.t(`dspBill.feeItemName.${id}`, { defaultValue: name });
}

const FILE_TYPE_TO_ID: Record<number, string> = {
  0: 'UNCLEAR',
  1: 'SUMMARY',
  2: 'DETAIL',
};

const FILE_TYPE_COLOR_MAP: Record<number, string> = {
  1: 'blue',
  2: 'green',
};

export function translateFileType(fileType: FileTypeCode | null | undefined): string {
  if (fileType === null || fileType === undefined) return '-';
  const id = FILE_TYPE_TO_ID[fileType];
  if (!id) return String(fileType);
  return i18n.t(`enum.fileType.${id}`, { defaultValue: id });
}

export function getFileTypeColor(fileType: FileTypeCode | null | undefined): string {
  if (fileType === null || fileType === undefined) return 'default';
  return FILE_TYPE_COLOR_MAP[fileType] || 'default';
}

const EXPORT_STATUS_COLOR_MAP: Record<string, string> = {
  PENDING: 'default',
  RUNNING: 'processing',
  SUCCESS: 'success',
  FAILED: 'error',
};

export function translateExportStatus(status: string | null | undefined): string {
  if (!status) return '-';
  return i18n.t(`enum.exportStatus.${status}`, { defaultValue: status });
}

export function getExportStatusColor(status: string | null | undefined): string {
  if (!status) return 'default';
  return EXPORT_STATUS_COLOR_MAP[status] || 'default';
}

/**
 * 已知 DSP 账单导出 currentStep（后端硬编码中文步骤）→ 稳定 ID 映射
 * 维护责任：后端新增导出步骤时，需同步更新此表 + i18n 三语 JSON dspBill.exportStep.*
 */
const CURRENT_STEP_TO_ID: Record<string, string> = {
  '开始导出': 'EXPORT_STARTED',
  '查询账单信息完成': 'BILL_INFO_QUERIED',
  '查询费用配置完成': 'FEE_CONFIG_QUERIED',
  '统计清分数据总量': 'COUNTING_SETTLEMENT_DATA',
  '统计完成，准备生成Excel文件': 'COUNTING_COMPLETED',
  '开始生成文件': 'FILE_GENERATION_STARTED',
  'Excel 写入中': 'EXCEL_WRITING',
  '开始读取清分数据': 'READING_SETTLEMENT_DATA',
  '文件生成完成': 'FILE_GENERATED',
  '文件上传完成':'FILE_UPLOAD_COMPLETE',
  'DSP Summary 附件生成完成': 'DSP_SUMMARY_ATTACHMENT_GENERATED',
};

export function translateCurrentStep(step: string | null | undefined): string {
  if (!step) return '-';
  const id = CURRENT_STEP_TO_ID[step];
  if (!id) {
    if (process.env.NODE_ENV !== 'production') {
      console.warn(
        `[billI18n] Unmapped currentStep: "${step}". Add to CURRENT_STEP_TO_ID and i18n JSON.`,
      );
    }
    return step;
  }
  return i18n.t(`dspBill.exportStep.${id}`, { defaultValue: step });
}

export const __testables = {
  FEE_ITEM_NAME_TO_ID,
  BILL_TYPE_COLOR_MAP,
  BILL_STATUS_COLOR_MAP,
  FILE_TYPE_TO_ID,
  FILE_TYPE_COLOR_MAP,
  EXPORT_STATUS_COLOR_MAP,
  CURRENT_STEP_TO_ID,
};
